import { useCallback, useEffect, useMemo } from "react";
import {
	FITBAnswer,
	MatchingAnswer,
	MatchingPair,
	MCOption,
	TFOption,
	VerityAssessment,
} from "@verity/api-client/types";
import { useQuizInfo } from "../hooks/useQuizInfo";
import {
	CoreButton,
	CoreCard,
	CoreBadge,
	CoreTextField,
	CoreSelect,
	CoreMenuItem,
	OverflowText,
} from "@verity/components/core";
import { MatchingDistractor, QuizQuestionType } from "../../assessments-page/verity-assessment/verityAssessmentHelper";
import styles from "./TakeQuizPage.module.css";
import i18n from "../../../../i18n";
import { baseTransKey } from "../takeAssessmentHelper";
import DOMPurify from "dompurify";
import "katex/dist/katex.min.css";

import MultipleChoiceIcon from "../../../../images/quiz-question/question-type-icon-multiple-choice.svg?react";
import TrueFalseIcon from "../../../../images/quiz-question/question-type-icon-true-false.svg?react";
import FillInTheBlankIcon from "../../../../images/quiz-question/question-type-icon-fill-in-the-blank.svg?react";
import MatchingIcon from "../../../../images/quiz-question/question-type-icon-matching.svg?react";
import { SharedTakeAssessmentProps } from "..";
import CountdownTimer from "./CountdownTimer";

export type TakeQuizQuestion = Omit<NonNullable<VerityAssessment["questions"]>[number], "questionType"> & {
	questionType: QuizQuestionType;
};

// ── Icon map ──────────────────────────────────────────────────────────────────────────────────
const QUESTION_TYPE_ICONS: Record<QuizQuestionType, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
	MultipleChoice: MultipleChoiceIcon,
	TrueFalse: TrueFalseIcon,
	FillInTheBlank: FillInTheBlankIcon,
	Matching: MatchingIcon,
};

// ── Option choices helper ─────────────────────────────────────────────────────────────────────
function getOptionChoices(question: TakeQuizQuestion): { letter: string; text: string }[] {
	if (question.questionType === "MultipleChoice") {
		const opts = question.options as MCOption[] | undefined;
		return (opts ?? []).map((opt, i) => ({ letter: String.fromCharCode(65 + i), text: opt.text }));
	}
	if (question.questionType === "TrueFalse") {
		const opts = question.options as TFOption[] | undefined;
		return (opts ?? []).map((opt, i) => ({ letter: String.fromCharCode(65 + i), text: opt.text }));
	}
	return [];
}

// ── FITB parsing ─────────────────────────────────────────────────────────────────────────────

type FITBSegment = { type: "html"; content: string } | { type: "blank"; blankId: number };

function parseFITBSegments(html: string): FITBSegment[] {
	if (typeof document === "undefined") return [{ type: "html", content: html }];
	const div = document.createElement("div");
	div.innerHTML = DOMPurify.sanitize(html, { ADD_ATTR: ["data-id"], ADD_TAGS: ["span"] });
	const PLACEHOLDER = "__FITB_BLANK_";
	div.querySelectorAll(".question-blank").forEach((span) => {
		const blankId = span.getAttribute("data-id") ?? "0";
		span.replaceWith(document.createTextNode(`${PLACEHOLDER}${blankId}__`));
	});
	const result: FITBSegment[] = [];
	// If the sanitized content is a single <p> block, unwrap it so segments stay inline.
	// Injecting "<p>...</p>" into a <span> is invalid HTML and causes line breaks.
	const root: Element =
		div.children.length === 1 && div.firstElementChild?.tagName === "P" ? (div.firstElementChild as HTMLElement) : div;
	let remaining = root.innerHTML;
	const regex = new RegExp(`${PLACEHOLDER}(\\d+)__`);
	while (remaining.length > 0) {
		const match = regex.exec(remaining);
		if (!match) {
			result.push({ type: "html", content: remaining });
			break;
		}
		if (match.index > 0) result.push({ type: "html", content: remaining.slice(0, match.index) });
		result.push({ type: "blank", blankId: parseInt(match[1]) });
		remaining = remaining.slice(match.index + match[0].length);
	}
	return result;
}

// ── FITB question component ───────────────────────────────────────────────────────────────────

function FITBQuestion({
	question,
	answer,
	onAnswerChange,
}: {
	question: TakeQuizQuestion;
	answer: FITBAnswer;
	onAnswerChange: (answer: FITBAnswer) => void;
}) {
	const segments = useMemo(() => parseFITBSegments(question.questionText), [question.questionText]);
	return (
		<div className={styles.fitbText}>
			{segments.map((seg, i) =>
				seg.type === "html" ? (
					<span
						key={i}
						className={styles.fitbSegment}
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(seg.content) }}
					/>
				) : (
					<CoreTextField
						key={i}
						size="small"
						className={styles.fitbInput}
						value={answer[seg.blankId] ?? ""}
						onChange={(e) => onAnswerChange({ ...answer, [seg.blankId]: e.target.value })}
						placeholder="Enter your answer here"
					/>
				),
			)}
		</div>
	);
}

// ── Matching question component ───────────────────────────────────────────────────────────────

function MatchingQuestion({
	question,
	answer,
	onAnswerChange,
}: {
	question: TakeQuizQuestion;
	answer: MatchingAnswer;
	onAnswerChange: (answer: MatchingAnswer) => void;
}) {
	const opts = (question.options as Array<MatchingPair | MatchingDistractor>) ?? [];
	const premises = opts.filter((o): o is MatchingPair => "premise" in o);
	const responses = opts.map((o) => o.response); // already shuffled in backend

	return (
		<div className={styles.matchingTable}>
			{premises.map((opt, i) => (
				<div key={i} className={styles.matchingRow}>
					<div
						className={styles.matchingPremise}
						dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(opt.premise) }}
					/>
					<span className={styles.matchingArrow}>&#x2192;</span>
					<CoreSelect
						className={styles.matchingSelect}
						value={answer[opt.premise] ?? ""}
						onChange={(e) => onAnswerChange({ ...answer, [opt.premise]: e.target.value })}
						displayEmpty
						renderValue={(value) => value || "Select an option"}
					>
						{responses.map((r, j) => (
							<CoreMenuItem key={j} value={r}>
								{r}
							</CoreMenuItem>
						))}
					</CoreSelect>
				</div>
			))}
		</div>
	);
}

// ── Circular progress ring ─────────────────────────────────────────────────────────────────────────
type ProgressState = "none" | "half" | "full";
function ProgressRing({ state }: { state: ProgressState }) {
	if (state === "half") {
		return (
			<svg
				width="18"
				height="32"
				viewBox="0 0 18 33"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className={styles.progressRing}
			>
				<path
					d="M1.5 32C3.53549 32 5.55105 31.5991 7.43159 30.8201C9.31214 30.0412 11.0208 28.8995 12.4602 27.4602C13.8995 26.0208 15.0412 24.3121 15.8201 22.4316C16.5991 20.551 17 18.5355 17 16.5C17 14.4645 16.5991 12.449 15.8201 10.5684C15.0412 8.68786 13.8995 6.97915 12.4602 5.53984C11.0208 4.10053 9.31214 2.95881 7.43159 2.17987C5.55104 1.40092 3.53548 0.999999 1.49999 1"
					stroke="#0B6F85"
					strokeWidth="2"
					strokeLinecap="round"
				></path>
			</svg>
		);
	} else if (state === "full") {
		return (
			<svg
				width="32"
				height="32"
				viewBox="0 0 32 32"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				className={styles.completedRing}
			>
				<circle cx="16" cy="16" r="15" fill="none" stroke="#0B6F85" strokeWidth="2" strokeLinecap="round" />
			</svg>
		);
	} else {
		return null;
	}
}

// ── Question sidebar nav item ─────────────────────────────────────────────────────────────────────────────

function QuestionNavItem({
	question,
	index,
	isActive,
	isAnswered,
	onClick,
}: {
	question: TakeQuizQuestion;
	index: number;
	isActive: boolean;
	isAnswered: boolean;
	onClick: () => void;
}) {
	const Icon = QUESTION_TYPE_ICONS[question.questionType];
	const progressState: ProgressState = isAnswered ? "full" : isActive ? "half" : "none";

	return (
		<button className={styles.navItem} onClick={onClick} aria-current={isActive ? "true" : undefined}>
			<div className={styles.iconWrapper}>
				<ProgressRing state={progressState} />
				<Icon className={styles.questionTypeIcon} />
			</div>
			<span className={styles.navLabel}>Question {index + 1}</span>
		</button>
	);
}

type TakeQuizPageProps = SharedTakeAssessmentProps;

export default function TakeQuizPage({
	quizName,
	proctoringSessionId,
	registerSubmitFn,
	timerParts,
	timerStatus,
}: TakeQuizPageProps) {
	const { questions, responses, updateResponse, currentIndex, setCurrentIndex, submitResponses, totalPoints } =
		useQuizInfo({
			proctoringSessionId,
		});

	const { endTime } = timerStatus;

	const currentQuestion = questions[currentIndex];
	const currentResponse = responses[currentIndex];
	const choices = currentQuestion ? getOptionChoices(currentQuestion) : [];
	const selectedOptionIndex =
		currentResponse?.isAnswered && Array.isArray(currentResponse.answer) ? (currentResponse.answer[0] ?? null) : null;

	const handleSubmit = useCallback(async () => {
		const scores = await submitResponses();
		return { success: true, ...scores };
	}, [submitResponses]);

	useEffect(() => {
		registerSubmitFn(handleSubmit);
	}, [registerSubmitFn, handleSubmit]);

	return (
		<div className={styles.takeQuizPage}>
			<CoreCard className={styles.quizCard} slotProps={{ bodyWrapper: { className: styles.quizCardWrapper } }}>
				<div className={styles.quizHeader}>
					<div className={styles.quizTitleRow}>
						<OverflowText className={styles.quizTitle}>{quizName} </OverflowText>
						<div className={styles.badges}>
							<CoreBadge className={`${styles.badge} ${styles.totalQuestions}`}>{questions.length} Questions</CoreBadge>
							<CoreBadge className={`${styles.badge} ${styles.totalPoints}`}>{totalPoints} Points</CoreBadge>
						</div>
					</div>
					<CountdownTimer timerParts={timerParts} endMs={endTime} />
				</div>

				<div className={styles.quizBody}>
					<nav className={styles.questionSidebar}>
						{questions.map((q, i) => (
							<QuestionNavItem
								key={q.id ?? q.questionOrder}
								question={q}
								index={i}
								isActive={i === currentIndex}
								isAnswered={responses[i]?.isAnswered ?? false}
								onClick={() => setCurrentIndex(i)}
							/>
						))}
					</nav>

					{currentQuestion && (
						<div className={styles.questionContent}>
							<div className={styles.questionMeta}>
								<span className={styles.questionNumber}>Question {currentIndex + 1}</span>
								<CoreBadge className={`${styles.badge} ${styles.questionPoints}`}>
									{currentQuestion.correctionPoints} Points
								</CoreBadge>
							</div>
							{currentQuestion.questionType !== "FillInTheBlank" && (
								<>
									<p className={styles.questionTypeLabel}>
										{i18n.t(`${baseTransKey}.questionType.${currentQuestion.questionType}`)}
									</p>
									<p
										className={styles.questionText}
										dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentQuestion.questionText) }}
									/>
								</>
							)}
							{(currentQuestion.questionType === "MultipleChoice" || currentQuestion.questionType === "TrueFalse") && (
								<div className={styles.optionsList}>
									{choices.map((choice, i) => (
										<button
											key={i}
											className={`${styles.optionButton} ${selectedOptionIndex === i ? styles.active : ""}`}
											onClick={() => updateResponse(currentIndex, [i])}
										>
											{currentQuestion.questionType === "MultipleChoice" && (
												<span className={styles.optionLetter}>{choice.letter}</span>
											)}
											{choice.text}
										</button>
									))}
								</div>
							)}
							{currentQuestion.questionType === "FillInTheBlank" && (
								<FITBQuestion
									question={currentQuestion}
									answer={(currentResponse?.answer as FITBAnswer) ?? {}}
									onAnswerChange={(ans) => updateResponse(currentIndex, ans)}
								/>
							)}
							{currentQuestion.questionType === "Matching" && (
								<MatchingQuestion
									question={currentQuestion}
									answer={(currentResponse?.answer as MatchingAnswer) ?? {}}
									onAnswerChange={(ans) => updateResponse(currentIndex, ans)}
								/>
							)}
						</div>
					)}

					<div className={styles.cardNavigation}>
						<CoreButton
							color="secondary"
							className={styles.navActionButton}
							disabled={currentIndex === 0}
							onClick={() => setCurrentIndex((i) => i - 1)}
						>
							Previous
						</CoreButton>
						<CoreButton
							color="primary"
							className={styles.navActionButton}
							disabled={currentIndex === questions.length - 1}
							onClick={() => setCurrentIndex((i) => i + 1)}
						>
							Next
						</CoreButton>
					</div>
				</div>
			</CoreCard>
		</div>
	);
}
